import type { VenueEventMessage, VenueKind } from "@townsy/contracts";
import { create } from "zustand";

/**
 * Attractions (ADR-0016): what the server last said about the venue game of the room the visitor is in. The shared
 * state and the visitor's own state are the server's; the browser only displays them. Cleared on every room change.
 */
export interface VenueSnapshot {
  venue: VenueKind | null;
  /** Sequence number of the last shared state (older broadcasts are ignored). */
  seq: number;
  shared: unknown;
  mine: unknown;
  /** Play chips (null where the venue has none, or before the server said). */
  chips: number | null;
  /** Latest notices, newest last (at most 8). */
  events: Array<VenueEventMessage & { id: number; at: number }>;
}

interface VenueStore extends VenueSnapshot {
  reset(venue: VenueKind | null): void;
  applyState(venue: VenueKind, seq: number, state: unknown): void;
  applyPrivate(venue: VenueKind, chips: number | null, state: unknown): void;
  pushEvent(event: VenueEventMessage): void;
}

const EMPTY: VenueSnapshot = { venue: null, seq: -1, shared: null, mine: null, chips: null, events: [] };
let eventId = 0;

export const useVenueStore = create<VenueStore>()((set) => ({
  ...EMPTY,
  reset: (venue) => set({ ...EMPTY, venue }),
  applyState: (venue, seq, state) =>
    set((s) => {
      if (s.venue !== venue) return s;
      if (seq <= s.seq) return s;
      return { seq, shared: state };
    }),
  applyPrivate: (venue, chips, state) => set((s) => (s.venue === venue ? { chips, mine: state } : s)),
  pushEvent: (event) =>
    set((s) => (s.venue === event.venue ? { events: [...s.events.slice(-7), { ...event, id: ++eventId, at: Date.now() }] } : s)),
}));
